import React, { useState, useEffect, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  pointerWithin,
  DragStartEvent,
  DragOverEvent,
  DragMoveEvent,
  DragEndEvent
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import ItineraryItemCard from '../components/ItineraryItemCard';
import SortableItineraryItem from '../components/SortableItineraryItem';
import { useNavigate, useParams } from 'react-router-dom';
import { TripPlannerAPI } from '../services/api';
import { useTripWithCache } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import MapView from '../components/MapView';
import AddDayModal from '../components/AddDayModal';
import AddItineraryItemModal from '../components/AddItineraryItemModal';
import RecommendationsModal from '../components/RecommendationsModal';
import EditDayModal from '../components/EditDayModal';
import EditTripModal from '../components/EditTripModal';
import DateScroller from '../components/DateScroller';

const TripViewPage: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  
  // Authentication
  const { user, loading: authLoading, getUserId, getUserName } = useAuth();
  
  // Log auth state for debugging
  useEffect(() => {
    console.log('🔍 TripViewPage auth state:', { 
      authLoading, 
      hasUser: !!user, 
      userId: getUserId(),
      tripId 
    });
  }, [authLoading, user, getUserId, tripId]);
  
  // Use the optimized hook for trip loading with caching
  // Only fetch when user is authenticated (not loading and user exists)
  const shouldFetchTrip = !authLoading && !!user;
  const { trip, loading, error, refetch, updateTrip } = useTripWithCache(tripId!, shouldFetchTrip);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());
  const [activeDragItem, setActiveDragItem] = useState<any | null>(null);
  const [hoverExpandTimer, setHoverExpandTimer] = useState<number | null>(null);
  const [dragOrderByDay, setDragOrderByDay] = useState<Record<number, string[]>>({});
  const [dragPlaceholderByDay, setDragPlaceholderByDay] = useState<Record<number, number | null>>({});
  
  // Selected day for date scroller (mobile-first design)
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null);
  
  // Scroll state for sticky behavior
  const [scrolled, setScrolled] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);
  
  // Date scroller scroll position state (to sync between fixed and regular)
  const [dateScrollerScrollLeft, setDateScrollerScrollLeft] = useState(0);
  // Fast auto-scroll during drag
  const isDraggingRef = useRef(false);
  const lastPointerYRef = useRef<number | null>(null);
  const lastPointerXRef = useRef<number | null>(null);
  const autoScrollRAFRef = useRef<number | null>(null);
  const lastOverDayIndexRef = useRef<number | null>(null);
  const initialPointerRef = useRef<{ x: number; y: number } | null>(null);
  const previousScrollBehaviorRef = useRef<{ html?: string | null; body?: string | null }>({});

  // Helper function for sorting days by date
  const sortDaysByDate = (days: any[]) => {
    return [...days].sort((a, b) => new Date(a.dayDate).getTime() - new Date(b.dayDate).getTime());
  };

  // Helper function to trim day title to 25 characters (safety measure)
  const trimDayTitle = (day: any): any => {
    if (!day || !day.title) return day;
    if (day.title.length > 25) {
      console.warn(`⚠️ Trimming day title from "${day.title}" (${day.title.length} chars) to "${day.title.substring(0, 25)}" (25 chars)`);
      return { ...day, title: day.title.substring(0, 25) };
    }
    return day;
  };

  // Helper function to check if a date string matches today (date only, ignoring time)
  const isToday = (dateString: string): boolean => {
    const today = new Date();
    const date = new Date(dateString);
    return (
      today.getFullYear() === date.getFullYear() &&
      today.getMonth() === date.getMonth() &&
      today.getDate() === date.getDate()
    );
  };

  // Sort days when trip is loaded and trim titles (safety measure for legacy data)
  useEffect(() => {
    if (trip?.days && trip.days.length > 0) {
      // Trim any titles that exceed 25 characters (safety measure)
      const trimmedDays = trip.days.map(day => trimDayTitle(day));
      const sortedDays = sortDaysByDate(trimmedDays);
      const needsUpdate = JSON.stringify(sortedDays) !== JSON.stringify(trip.days);
      if (needsUpdate) {
        updateTrip(currentTrip => {
          if (!currentTrip) return currentTrip;
          return {
            ...currentTrip,
            days: sortedDays
          };
        });
      }
      // Set selected day: prefer today if it's in the trip, otherwise first day
      if (!selectedDayId && sortedDays.length > 0) {
        // Find today's day if it exists in the trip
        const todayDay = sortedDays.find(day => isToday(day.dayDate));
        if (todayDay) {
          setSelectedDayId(todayDay.dayId);
        } else {
          // Fall back to first day
          setSelectedDayId(sortedDays[0].dayId);
        }
      }
    }
  }, [trip?.days, updateTrip, selectedDayId]);

  
  // Modal states
  const [isAddDayModalOpen, setIsAddDayModalOpen] = useState(false);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [isRecommendationsModalOpen, setIsRecommendationsModalOpen] = useState(false);
  const [isEditDayModalOpen, setIsEditDayModalOpen] = useState(false);
  const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false);
  const [prepopulatedStartTime, setPrepopulatedStartTime] = useState<string | undefined>(undefined);
  const [selectedDayIndexForModal, setSelectedDayIndexForModal] = useState<number>(0);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isSavingDay, setIsSavingDay] = useState(false);
  const [isSavingItem, setIsSavingItem] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isUpdatingDay, setIsUpdatingDay] = useState(false);
  const [isDeletingDay, setIsDeletingDay] = useState(false);
  const [isUpdatingTrip, setIsUpdatingTrip] = useState(false);
  const [isDeletingTrip, setIsDeletingTrip] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  
  // AddItineraryItemModal form state (managed in parent to persist across remounts)
  const [addItemFormData, setAddItemFormData] = useState({
    name: '',
    placeName: '',
    description: '',
    startTime: '',
    endTime: '',
    location: { lat: 0, lon: 0 },
    itemType: '',
    estimatedCost: 0,
    aiEditPrompt: '',
    mode: 'recommendations' as 'recommendations' | 'ai' | 'manual',
    isAIMode: true
  });
  
  // Track if form has been initialized for current modal session
  const addItemFormInitializedRef = useRef(false);
  
  // Initialize form data when modal opens (in parent to persist across remounts)
  // Only initialize once when modal opens, not on every render or prop change
  useEffect(() => {
    if (isAddItemModalOpen && !addItemFormInitializedRef.current) {
      // Check if form is empty (user hasn't filled it yet) - only initialize if empty
      const formIsEmpty = !addItemFormData.name && !addItemFormData.placeName && 
                          !addItemFormData.description && !addItemFormData.itemType &&
                          addItemFormData.estimatedCost === 0;
      
      if (formIsEmpty) {
        addItemFormInitializedRef.current = true;
        
        const targetDay = trip?.days?.[selectedDayIndexForModal];
        
        if (editingItem) {
          // Editing mode - populate with existing item data
          setAddItemFormData({
            name: editingItem.name || '',
            placeName: editingItem.placeName || '',
            description: editingItem.description || '',
            startTime: editingItem.startTime || '',
            endTime: editingItem.endTime || '',
            itemType: editingItem.itemType || '',
            estimatedCost: editingItem.estimatedCost || 0,
            location: {
              lat: editingItem.location?.lat || 0,
              lon: editingItem.location?.lon || 0
            },
            aiEditPrompt: '',
            mode: 'recommendations',
            isAIMode: true
          });
        } else if (targetDay) {
          // Adding new item - set default start time
          const getLastTimeInDay = () => {
            if (!targetDay.itinerary || targetDay.itinerary.length === 0) {
              return '09:00';
            }
            const lastItem = targetDay.itinerary[targetDay.itinerary.length - 1];
            if (lastItem.endTime) {
              const [hours, minutes] = lastItem.endTime.split(':').map(Number);
              const nextHour = (hours + 1) % 24;
              return `${String(nextHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            }
            return '09:00';
          };
          
          const initialStartTime = prepopulatedStartTime || getLastTimeInDay();
          setAddItemFormData(prev => ({
            ...prev,
            startTime: initialStartTime,
            mode: 'recommendations',
            name: '',
            placeName: '',
            description: '',
            endTime: '',
            itemType: '',
            estimatedCost: 0,
            location: { lat: 0, lon: 0 },
            aiEditPrompt: '',
            isAIMode: true
          }));
        }
      }
    } else if (!isAddItemModalOpen) {
      // Reset initialization flag when modal closes
      addItemFormInitializedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddItemModalOpen]); // ONLY depend on isAddItemModalOpen - don't re-initialize on prop changes
  
  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastTimeout, setToastTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  
  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeout) {
        clearTimeout(toastTimeout);
      }
    };
  }, [toastTimeout]);
  
  const [isDeletingItem, setIsDeletingItem] = useState(false);

  // Mobile-specific state
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState<'itinerary' | 'map'>('itinerary');
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [selectedMapItem, setSelectedMapItem] = useState<any>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);

  // Scroll to selected item in mobile modal when it opens
  useEffect(() => {
    if (isMobile && bottomSheetOpen && selectedMapItem) {
      setTimeout(() => {
        const element = document.getElementById(`mobile-item-${selectedMapItem.itemId}`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 300); // Delay to ensure modal is fully rendered
    }
  }, [isMobile, bottomSheetOpen, selectedMapItem]);

  // Scroll to focused item when switching from map to itinerary in mobile mode
  useEffect(() => {
    if (isMobile && mobileView === 'itinerary' && selectedItemId) {
      setTimeout(() => {
        const element = document.getElementById(`item-${selectedItemId}`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 200); // Delay to ensure view switch is complete
    }
  }, [isMobile, mobileView, selectedItemId]);

  // Close mobile map modal when exiting map view
  useEffect(() => {
    if (isMobile && mobileView !== 'map') {
      setBottomSheetOpen(false);
      setSelectedMapItem(null);
    }
  }, [isMobile, mobileView]);

  // Close add menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAddMenu) {
        const menu = document.querySelector('[data-add-menu]');
        const button = document.querySelector('[data-add-button]');
        
        if (menu && !menu.contains(event.target as Node) && 
            button && !button.contains(event.target as Node)) {
          setShowAddMenu(false);
        }
      }
    };

    if (showAddMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddMenu]);

  // Trip loading is now handled by the useTripWithCache hook

  // Mobile detection
  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth < 768;
      console.log('📱 Mobile detection:', mobile, 'width:', window.innerWidth);
      setIsMobile(mobile);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Scroll detection for sticky behavior
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const scrollPosition = window.scrollY;
        const currentScrollY = window.scrollY;
        
        // Trigger sticky behavior when scrolled past 60% of header height
        setScrolled(scrollPosition > headerHeight * 0.6);
        
        // Show sticky header when scrolling down past hero section, hide when scrolling up
        if (isMobile) {
          const heroHeight = window.innerHeight * 0.5; // 50vh
          if (currentScrollY > heroHeight * 0.7) {
            // Scrolled down - show sticky header
            if (currentScrollY > lastScrollY) {
              setShowStickyHeader(true);
            } else if (currentScrollY < heroHeight * 0.5) {
              // Scrolled back up near top - hide sticky header
              setShowStickyHeader(false);
            }
          } else {
            setShowStickyHeader(false);
          }
        }
        
        setLastScrollY(currentScrollY);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, lastScrollY]);

  // Note: Removed automatic re-check to prevent infinite loops
  // Use manual buttons in the UI to trigger auth checks when needed

  // Mobile handlers
  const handleMapItemClick = (itemId: string) => {
    console.log('🗺️ Map item clicked:', itemId);
    if (!trip) {
      console.log('❌ No trip data available');
      return;
    }
    
    // Find the item by ID from all days
    const allItems = trip.days.flatMap(day => day.itinerary || []);
    console.log('📋 All items:', allItems.length);
    const item = allItems.find(it => it.itemId === itemId);
    console.log('🎯 Found item:', item);
    
    if (item) {
      setSelectedMapItem(item);
      setBottomSheetOpen(true);
      
      // Also set the selected item ID for highlighting and scrolling
      setSelectedItemId(itemId);
      
      // Find which day contains this item and expand it
      for (let dayIndex = 0; dayIndex < trip.days.length; dayIndex++) {
        const day = trip.days[dayIndex];
        if (day.itinerary?.some(item => item.itemId === itemId)) {
          // Expand the day
          setExpandedDays(prev => new Set(prev).add(dayIndex));
          break;
        }
      }
      
      // Scroll to the selected item in the left panel
      setTimeout(() => {
        const element = document.getElementById(`item-${itemId}`);
        if (element) {
          // Use scrollIntoView for simpler, more reliable scrolling
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 200); // Slightly longer delay for mobile to ensure day expansion completes
      
      console.log('✅ Bottom sheet opened and scrolled to item:', item.name);
    } else {
      console.log('❌ Item not found with ID:', itemId);
    }
  };

  // Find the day and day index that contains the selected item
  const getDayForItem = (item: any) => {
    if (!trip || !item) return { day: null, dayIndex: -1 };
    
    for (let dayIndex = 0; dayIndex < trip.days.length; dayIndex++) {
      const day = trip.days[dayIndex];
      if (day.itinerary && day.itinerary.some((itineraryItem: any) => itineraryItem.itemId === item.itemId)) {
        return { day, dayIndex };
      }
    }
    return { day: null, dayIndex: -1 };
  };

  const handleCloseBottomSheet = () => {
    setBottomSheetOpen(false);
    setSelectedMapItem(null);
  };

  // Handle bottom sheet swipe down to close
  const handleBottomSheetTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const startY = touch.clientY;
    
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const currentY = touch.clientY;
      const deltaY = currentY - startY;
      
      if (deltaY > 50) {
        handleCloseBottomSheet();
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      }
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  // Helper functions for optimistic updates
  const findDayIndex = (days: any[], dayId: string) => {
    return days.findIndex(day => day.dayId === dayId);
  };

  const focusOnDay = (dayId: string) => {
    if (!trip) return;
    const dayIndex = findDayIndex(trip.days, dayId);
    if (dayIndex !== -1) {
      setSelectedDayIndexForModal(dayIndex);
      setSelectedDayId(dayId);
    }
  };

  const addDayToTrip = (newDay: any) => {
    // Trim title if it exceeds 25 characters (safety measure for AI-generated data)
    const trimmedDay = trimDayTitle(newDay);
    updateTrip(currentTrip => {
      if (!currentTrip) return currentTrip;
      const updatedDays = [...(currentTrip.days || []), trimmedDay];
      const sortedDays = sortDaysByDate(updatedDays);
      const updatedTrip = {
        ...currentTrip,
        days: sortedDays
      };
      
      // Focus on the newly added day after state update
      // Use a longer timeout to ensure DOM updates and DateScroller renders
      setTimeout(() => {
        const dayIndex = sortedDays.findIndex(d => d.dayId === trimmedDay.dayId);
        if (dayIndex !== -1) {
          setSelectedDayIndexForModal(dayIndex);
          setSelectedDayId(trimmedDay.dayId);
        }
      }, 200);
      
      return updatedTrip;
    });
  };

  const addItemToDay = (dayIndex: number, newItem: any) => {
    updateTrip(currentTrip => {
      if (!currentTrip) return currentTrip;
      return {
        ...currentTrip,
        days: currentTrip.days?.map((day, index) => 
          index === dayIndex 
            ? { ...day, itinerary: [...(day.itinerary || []), newItem] }
            : day
        ) || []
      };
    });
  };

  const updateDayInTrip = (dayIndex: number, updatedDay: any) => {
    // Trim title if it exceeds 25 characters (safety measure for AI-generated data)
    const trimmedDay = trimDayTitle(updatedDay);
    updateTrip(currentTrip => {
      if (!currentTrip) return currentTrip;
      const updatedDays = currentTrip.days.map((day, index) => 
        index === dayIndex ? trimmedDay : day
      );
      const sortedDays = sortDaysByDate(updatedDays);
      return {
        ...currentTrip,
        days: sortedDays
      };
    });
    // Focus on the updated day
    setTimeout(() => focusOnDay(trimmedDay.dayId), 100);
  };

  const deleteDayFromTrip = (dayIndex: number) => {
    updateTrip(currentTrip => {
      if (!currentTrip) return currentTrip;
      return {
        ...currentTrip,
        days: currentTrip.days.filter((_, index) => index !== dayIndex)
      };
    });
  };

  const updateItemInDay = (dayIndex: number, itemId: string, updatedItem: any) => {
    updateTrip(currentTrip => {
      if (!currentTrip) return currentTrip;
      return {
        ...currentTrip,
        days: currentTrip.days.map((day, dayIdx) => 
          dayIdx === dayIndex
            ? {
                ...day,
                itinerary: day.itinerary.map((item) =>
                  item.itemId === itemId ? { ...item, ...updatedItem } : item
                )
              }
            : day
        )
      };
    });
  };

  const deleteItemFromDay = (dayIndex: number, itemId: string) => {
    updateTrip(currentTrip => {
      if (!currentTrip) return currentTrip;
      return {
        ...currentTrip,
        days: currentTrip.days.map((day, dayIdx) => 
          dayIdx === dayIndex
            ? {
                ...day,
                itinerary: day.itinerary.filter((item) => item.itemId !== itemId)
              }
            : day
        )
      };
    });
  };

  const handleItemSelect = (itemId: string) => {
    setSelectedItemId(itemId);
    
    // Find which day contains this item and expand it
    if (trip?.days) {
      for (let dayIndex = 0; dayIndex < trip.days.length; dayIndex++) {
        const day = trip.days[dayIndex];
        if (day.itinerary?.some(item => item.itemId === itemId)) {
          // Expand the day
          setExpandedDays(prev => new Set(prev).add(dayIndex));
          break;
        }
      }
    }
    
    // Scroll to the selected item in the left panel
    setTimeout(() => {
      const element = document.getElementById(`item-${itemId}`);
      if (element) {
        // Use scrollIntoView for simpler, more reliable scrolling
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 100); // Small delay to ensure DOM updates are complete
  };

  // DnD sensors - configure for both desktop and mobile
  const sensors = useSensors(
    useSensor(PointerSensor, { 
      activationConstraint: { distance: 6 }
    }),
    useSensor(TouchSensor, {
      activationConstraint: { 
        delay: 100, // Short delay to distinguish from tap
        tolerance: 8 // Allow small movement before activating
      }
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Helpers for time calculations
  const parseTime = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map((v) => parseInt(v, 10));
    return h * 60 + m;
  };
  const formatTimeHM = (mins: number) => {
    const hh = Math.max(0, Math.floor(mins / 60));
    const mm = Math.max(0, mins % 60);
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${pad(hh)}:${pad(mm)}`;
  };

  const getDayIndexByContainerId = (containerId: string): number => {
    if (!trip) return -1;
    const key = containerId.replace('day-', '');
    return trip.days.findIndex((d) => d.dayId === key);
  };

  const findItemLocation = (itemId: string): { dayIndex: number; index: number } => {
    if (!trip) return { dayIndex: -1, index: -1 };
    for (let d = 0; d < trip.days.length; d++) {
      const idx = trip.days[d].itinerary.findIndex((it) => it.itemId === itemId);
      if (idx !== -1) return { dayIndex: d, index: idx };
    }
    return { dayIndex: -1, index: -1 };
  };

  // Note: insertion index is derived from current visual order + placeholder during drag

  const getOrderForDay = (dayIndex: number): string[] => {
    if (!trip) return [];
    const provided = dragOrderByDay[dayIndex];
    if (provided && provided.length) return provided;
    // default order: by start time
    return [...trip.days[dayIndex].itinerary]
      .sort((a, b) => a.startTime.localeCompare(b.startTime))
      .map((it) => it.itemId);
  };

  const setOrderForDay = (dayIndex: number, order: string[]) => {
    setDragOrderByDay((prev) => ({ ...prev, [dayIndex]: order }));
  };

  const setPlaceholderForDay = (dayIndex: number, index: number | null) => {
    setDragPlaceholderByDay((prev) => ({ ...prev, [dayIndex]: index }));
  };

  const onDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    if (!trip) return;
    const { dayIndex, index } = findItemLocation(activeId);
    if (dayIndex !== -1 && index !== -1) {
      setActiveDragItem(trip.days[dayIndex].itinerary[index]);
      // initialize source day order for visual displacement
      setOrderForDay(dayIndex, getOrderForDay(dayIndex));
    }

    // Begin aggressive auto-scroll loop
    isDraggingRef.current = true;
    const updatePointerCoords = (x: number, y: number) => {
      lastPointerYRef.current = y;
      lastPointerXRef.current = x;
    };
    // No global move listeners; we track pointer via DnD deltas and activator event

    // Disable smooth scrolling during drag so auto-scroll is immediate
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    previousScrollBehaviorRef.current = {
      html: htmlEl?.style.scrollBehavior ?? null,
      body: bodyEl?.style.scrollBehavior ?? null,
    };
    if (htmlEl) htmlEl.style.scrollBehavior = 'auto';
    if (bodyEl) bodyEl.style.scrollBehavior = 'auto';

    const activator = event.activatorEvent as any;
    if (activator) {
      if (typeof activator.clientX === 'number' && typeof activator.clientY === 'number') {
        initialPointerRef.current = { x: activator.clientX, y: activator.clientY };
        updatePointerCoords(activator.clientX, activator.clientY);
      } else if (activator.touches && activator.touches[0]) {
        const touch = activator.touches[0];
        initialPointerRef.current = { x: touch.clientX, y: touch.clientY };
        updatePointerCoords(touch.clientX, touch.clientY);
      }
    }

    const autoScrollStep = () => {
      if (!isDraggingRef.current) return;
      const y = lastPointerYRef.current;
      const vh = window.innerHeight;
      const scrollEl = document.scrollingElement || document.documentElement;
      if (typeof y === 'number' && scrollEl) {
        // Use viewport edges; scroll whole document body
        // Very small edge zones (10% of viewport) – only the top/bottom 10% triggers scroll
        const topZone = Math.max(60, Math.floor(vh * 0.1));
        const bottomZone = vh - topZone;
        let deltaY = 0;

        // When dragging outside viewport, extremely slow speed
        if (y < 0) {
          const distanceOutside = Math.abs(y);
          deltaY = -Math.min(8, 2 + distanceOutside * 0.04);
        } else if (y > vh) {
          const distanceOutside = y - vh;
          deltaY = Math.min(8, 2 + distanceOutside * 0.04);
        } else if (y < topZone) {
          // Inside viewport, top edge zone - extremely slow
          const intensity = (topZone - y) / topZone; // 0..1
          const accel = Math.pow(intensity, 1.1); // very gentle ramp
          deltaY = -Math.min(6, 2 + accel * 3);
        } else if (y > bottomZone) {
          // Inside viewport, bottom edge zone - extremely slow
          const intensity = (y - bottomZone) / (vh - bottomZone);
          const accel = Math.pow(intensity, 1.1);
          deltaY = Math.min(6, 2 + accel * 3);
        }
        if (deltaY !== 0) {
          const current = scrollEl.scrollTop ?? document.documentElement.scrollTop ?? window.scrollY ?? 0;
          const maxScrollTop = Math.max(0, (scrollEl.scrollHeight || document.documentElement.scrollHeight || document.body.scrollHeight || 0) - vh);
          const next = Math.max(0, Math.min(maxScrollTop, current + deltaY));
          if (next !== current) {
            scrollEl.scrollTop = next;
            if (scrollEl === document.documentElement && document.body) {
              document.body.scrollTop = next;
            }
          }
        }
      }
      autoScrollRAFRef.current = window.requestAnimationFrame(autoScrollStep);
    };
    autoScrollRAFRef.current = window.requestAnimationFrame(autoScrollStep);
  };

  const onDragOver = (event: DragOverEvent) => {
    const { over, active } = event;
    if (!over) return;
    const overId = String(over.id);
    // Auto-expand day when hovering its container while collapsed
    if (overId.startsWith('day-')) {
      const targetDayIndex = getDayIndexByContainerId(overId);
      if (targetDayIndex !== -1 && !expandedDays.has(targetDayIndex)) {
        if (hoverExpandTimer) {
          window.clearTimeout(hoverExpandTimer);
        }
        const t = window.setTimeout(() => {
          setExpandedDays((prev) => new Set(prev).add(targetDayIndex));
        }, 350);
        setHoverExpandTimer(t as unknown as number);
      }
    }

    // Update temporary orders so siblings move out of the way
    if (!trip || !active?.id) return;
    const activeId = String(active.id);
    const { dayIndex: fromDayIndex } = findItemLocation(activeId);
    if (fromDayIndex === -1) return;

    let toDayIndex = fromDayIndex;
    let overIndexInTarget = -1;
    if (overId.startsWith('day-')) {
      toDayIndex = getDayIndexByContainerId(overId);
      overIndexInTarget = getOrderForDay(toDayIndex).length; // end of list
    } else {
      const { dayIndex: overDayIndex } = findItemLocation(overId);
      toDayIndex = overDayIndex;
    // track last over day for fallback drop
    lastOverDayIndexRef.current = toDayIndex;
      const targetOrder = getOrderForDay(toDayIndex);
      overIndexInTarget = targetOrder.indexOf(overId);
      if (overIndexInTarget === -1) overIndexInTarget = targetOrder.length;
    }

    // If same day, reorder within that order
    if (toDayIndex === fromDayIndex) {
      const currentOrder = getOrderForDay(fromDayIndex);
      const fromIdx = currentOrder.indexOf(activeId);
      const toIdx = overIndexInTarget;
      if (fromIdx !== -1 && toIdx !== -1 && fromIdx !== toIdx) {
        setOrderForDay(fromDayIndex, arrayMove(currentOrder, fromIdx, toIdx));
      }
      setPlaceholderForDay(fromDayIndex, toIdx);
    } else {
      // Moving across days: remove from source, insert into target at index
      const sourceOrder = getOrderForDay(fromDayIndex);
      const fromIdx = sourceOrder.indexOf(activeId);
      if (fromIdx !== -1) {
        const newSource = [...sourceOrder];
        newSource.splice(fromIdx, 1);
        setOrderForDay(fromDayIndex, newSource);
        setPlaceholderForDay(toDayIndex, overIndexInTarget);
      }
    }
  };

  // Ensure we continuously update pointer Y during drag for autoscroll
  const onDragMove = (event: DragMoveEvent) => {
    const start = initialPointerRef.current;
    if (start) {
      lastPointerYRef.current = start.y + event.delta.y;
      lastPointerXRef.current = start.x + event.delta.x;
      return;
    }
    const ae: any = (event as any).activatorEvent;
    if (ae) {
      if (typeof ae.clientY === 'number' && typeof ae.clientX === 'number') {
        lastPointerYRef.current = ae.clientY;
        lastPointerXRef.current = ae.clientX;
        return;
      }
      if (ae.touches && ae.touches[0]) {
        lastPointerYRef.current = ae.touches[0].clientY;
        lastPointerXRef.current = ae.touches[0].clientX;
        return;
      }
    }
    const over = (event as any).over;
    const rectTop = over?.rect?.top;
    if (typeof rectTop === 'number') {
      lastPointerYRef.current = rectTop;
    }
  };

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragItem(null);
    // Stop aggressive auto-scroll immediately
    isDraggingRef.current = false;
    initialPointerRef.current = null;
    if (autoScrollRAFRef.current) {
      cancelAnimationFrame(autoScrollRAFRef.current);
      autoScrollRAFRef.current = null;
    }
    // Remove global listeners if present
    // No global listeners to remove – autoscroll uses drag deltas only
    // Restore previous smooth-scroll settings
    const behavior = previousScrollBehaviorRef.current;
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    if (htmlEl && Object.prototype.hasOwnProperty.call(behavior, 'html')) {
      if (!behavior.html) htmlEl.style.removeProperty('scroll-behavior');
      else htmlEl.style.scrollBehavior = behavior.html;
    }
    if (bodyEl && Object.prototype.hasOwnProperty.call(behavior, 'body')) {
      if (!behavior.body) bodyEl.style.removeProperty('scroll-behavior');
      else bodyEl.style.scrollBehavior = behavior.body;
    }
    previousScrollBehaviorRef.current = {};
    if (!trip) {
      setDragOrderByDay({});
      setDragPlaceholderByDay({});
      lastOverDayIndexRef.current = null;
      return;
    }

    const activeId = String(active.id);
    const overId = over ? String(over.id) : '';

    // Determine source location
    const { dayIndex: fromDayIndex, index: fromIndex } = findItemLocation(activeId);
    if (fromDayIndex === -1 || fromIndex === -1) {
      setDragOrderByDay({});
      setDragPlaceholderByDay({});
      lastOverDayIndexRef.current = null;
      return;
    }
    // const movingItem = trip.days[fromDayIndex].itinerary[fromIndex];

    // Determine target day and index
    let toDayIndex = fromDayIndex;
    let toIndex = fromIndex;

    if (over && overId.startsWith('day-')) {
      toDayIndex = getDayIndexByContainerId(overId);
      const ph = dragPlaceholderByDay[toDayIndex] ?? null;
      const targetOrder = getOrderForDay(toDayIndex);
      toIndex = ph !== null ? ph : targetOrder.length;
    } else if (over) {
      const { dayIndex: overDayIndex } = findItemLocation(overId);
      toDayIndex = overDayIndex;
      const targetOrder = getOrderForDay(toDayIndex);
      const idxOver = targetOrder.indexOf(overId);
      toIndex = idxOver === -1 ? targetOrder.length : idxOver;
    } else {
      // Fallback: no "over" target. Use last hovered day and its placeholder if available
      const fallbackDay = lastOverDayIndexRef.current;
      if (fallbackDay !== null && fallbackDay !== -1) {
        toDayIndex = fallbackDay;
        const ph = dragPlaceholderByDay[toDayIndex] ?? null;
        const targetOrder = getOrderForDay(toDayIndex);
        toIndex = ph !== null ? ph : targetOrder.length;
      } else {
        // As a final fallback, drop back to source day at current order index
        const currentOrder = getOrderForDay(fromDayIndex);
        const idx = currentOrder.indexOf(activeId);
        toIndex = idx !== -1 ? idx : fromIndex;
      }
    }

    // If reordering inside the same day, trust the current visual order's index of the active item
    if (toDayIndex === fromDayIndex) {
      const currentOrder = getOrderForDay(fromDayIndex);
      const visualIndex = currentOrder.indexOf(activeId);
      if (visualIndex !== -1) toIndex = visualIndex;
    }

    // Clear temporary visual orders and placeholders FIRST, before any early returns
    setDragOrderByDay({});
    setDragPlaceholderByDay({});
    lastOverDayIndexRef.current = null;

    // If same location, no-op (but state is already cleared above)
    if (fromDayIndex === toDayIndex && fromIndex === toIndex) return;

    // Compute new arrays optimistically and time adjustments
    const targetDay = trip.days[toDayIndex];
    const sourceDay = trip.days[fromDayIndex];
    const sourceItems = [...sourceDay.itinerary];
    // Remove from source
    const [removed] = sourceItems.splice(fromIndex, 1);

    let targetItems = fromDayIndex === toDayIndex ? sourceItems : [...targetDay.itinerary];
    // Insert into target at toIndex (clamp)
    const clampedIndex = Math.max(0, Math.min(toIndex, targetItems.length));
    targetItems.splice(clampedIndex, 0, removed);

    // Time logic
    const getDefaultStartForIndex = (items: any[], idx: number): string => {
      if (idx === 0) return '09:00';
      const prev = items[idx - 1];
      return prev?.endTime?.slice(0, 5) || '09:00';
    };
    const durationMins = Math.max(15, parseTime(removed.endTime) - parseTime(removed.startTime));
    const newStart = getDefaultStartForIndex(targetItems, clampedIndex);
    const newEnd = formatTimeHM(parseTime(newStart) + durationMins);

    // Determine if shifting is relevant: inserting in middle with following items existing
    const insertingAtEnd = clampedIndex >= targetItems.length - 1;
    const needShiftPrompt = !insertingAtEnd && targetItems.length > 1;
    let shiftFollowing = false;
    if (needShiftPrompt) {
      shiftFollowing = window.confirm('Adjust times of following items to accommodate this move?');
    }

    // Apply times and optionally shift following items to keep sequence valid
    if (fromDayIndex === toDayIndex) {
      // Same-day reorder
      const updatedItems = targetItems.map((it) => {
        if (it.itemId !== removed.itemId) return it;
        return { ...it, startTime: newStart, endTime: newEnd };
      });
      if (shiftFollowing) {
        for (let i = clampedIndex + 1; i < updatedItems.length; i++) {
          const prev = updatedItems[i - 1];
          const cur = updatedItems[i];
          const curDuration = Math.max(15, parseTime(cur.endTime) - parseTime(cur.startTime));
          const s = prev.endTime;
          const e = formatTimeHM(parseTime(s) + curDuration);
          updatedItems[i] = { ...cur, startTime: s, endTime: e };
        }
      }
      // Persist optimistically
      updateTrip(current => {
        if (!current) return current;
        return {
          ...current,
          days: current.days.map((d, idx) => idx === toDayIndex ? { ...d, itinerary: updatedItems } : d)
        };
      });

      // Persist to backend: update changed items
      try {
        const dayId = trip.days[toDayIndex].dayId;
        // Update moved item
        await TripPlannerAPI.updateItem(trip.tripId, dayId, removed.itemId, {
          name: removed.name,
          placeName: removed.placeName,
          description: removed.description,
          startTime: newStart,
          endTime: newEnd,
          location: removed.location,
          itemType: removed.itemType,
          estimatedCost: removed.estimatedCost,
          officialUrl: removed.officialUrl,
          isDone: removed.isDone ?? false,
        });
        // Update following items if shifted
        if (shiftFollowing) {
          for (let i = clampedIndex + 1; i < updatedItems.length; i++) {
            const it = updatedItems[i];
            await TripPlannerAPI.updateItem(trip.tripId, dayId, it.itemId, {
              name: it.name,
              placeName: it.placeName,
              description: it.description,
              startTime: it.startTime,
              endTime: it.endTime,
              location: it.location,
              itemType: it.itemType,
              estimatedCost: it.estimatedCost,
              officialUrl: it.officialUrl,
              isDone: it.isDone ?? false,
            });
          }
        }
      } catch (e) {
        console.error('Failed to persist reorder:', e);
      }
    } else {
      // Cross-day move: remove from source, insert into target, set times; optional shift in target
      let updatedSourceItems = [...sourceDay.itinerary];
      updatedSourceItems.splice(fromIndex, 1);
      let updatedTargetItems = [...targetItems].map((it) => ({ ...it }));
      const movedIdx = updatedTargetItems.findIndex((it) => it.itemId === removed.itemId);
      if (movedIdx !== -1) {
        updatedTargetItems[movedIdx].startTime = newStart;
        updatedTargetItems[movedIdx].endTime = newEnd;
      }
      if (shiftFollowing) {
        for (let i = movedIdx + 1; i < updatedTargetItems.length; i++) {
          const prev = updatedTargetItems[i - 1];
          const cur = updatedTargetItems[i];
          const curDuration = Math.max(15, parseTime(cur.endTime) - parseTime(cur.startTime));
          const s = prev.endTime;
          const e = formatTimeHM(parseTime(s) + curDuration);
          updatedTargetItems[i] = { ...cur, startTime: s, endTime: e };
        }
      }

      updateTrip(current => {
        if (!current) return current;
        return {
          ...current,
          days: current.days.map((d, idx) => {
            if (idx === fromDayIndex) return { ...d, itinerary: updatedSourceItems };
            if (idx === toDayIndex) return { ...d, itinerary: updatedTargetItems };
            return d;
          })
        };
      });

      // Persist: delete from source day, add to target day with new times, then update shifted items
      try {
        await TripPlannerAPI.deleteItem(trip.tripId, trip.days[fromDayIndex].dayId, removed.itemId);
        const addResp = await TripPlannerAPI.addItemToDay(trip.tripId, trip.days[toDayIndex].dayId, {
          name: removed.name,
          placeName: removed.placeName,
          description: removed.description,
          startTime: newStart,
          endTime: newEnd,
          location: removed.location,
          itemType: removed.itemType,
          estimatedCost: removed.estimatedCost,
          isDone: removed.isDone ?? false,
        });
        const newItem = addResp.item;
        // Update local state to replace temp itemId with backend new id
        updateTrip(current => {
          if (!current) return current;
          const days = [...current.days];
          const tItems = [...days[toDayIndex].itinerary];
          const idx = tItems.findIndex((it) => it.itemId === removed.itemId);
          if (idx !== -1) {
            tItems[idx] = { ...newItem };
            days[toDayIndex] = { ...days[toDayIndex], itinerary: tItems };
          }
          return { ...current, days };
        });
        // Persist shifts
        if (shiftFollowing) {
          for (let i = movedIdx + 1; i < updatedTargetItems.length; i++) {
            const it = updatedTargetItems[i];
            // Skip if it's the moved item replaced above; ensure we use current id
            await TripPlannerAPI.updateItem(trip.tripId, trip.days[toDayIndex].dayId, it.itemId, {
              name: it.name,
              placeName: it.placeName,
              description: it.description,
              startTime: it.startTime,
              endTime: it.endTime,
              location: it.location,
              itemType: it.itemType,
              estimatedCost: it.estimatedCost,
              officialUrl: it.officialUrl,
              isDone: it.isDone ?? false,
            });
          }
        }
      } catch (e) {
        console.error('Failed to persist cross-day move:', e);
      }
    }
  };

  const handleAddDay = () => {
    setIsAddDayModalOpen(true);
  };

  const handleAddItineraryItem = (dayIndex: number, suggestedStartTime?: string) => {
    if (!trip || !trip.days || dayIndex < 0 || dayIndex >= trip.days.length) {
      return;
    }
    
    const day = trip.days[dayIndex];
    const hasNoActivities = !day.itinerary || day.itinerary.length === 0;
    
    setSelectedDayIndexForModal(dayIndex);
    
    // If day has no activities, open RecommendationsModal instead
    if (hasNoActivities) {
      setIsRecommendationsModalOpen(true);
    } else {
      // Day has activities, open the manual AddItineraryItemModal
      if (suggestedStartTime) {
        setPrepopulatedStartTime(suggestedStartTime);
      } else {
        setPrepopulatedStartTime(undefined);
      }
      setIsAddItemModalOpen(true);
    }
  };

  const handleAddActivity = async (dayIndex: number, newItem: any) => {
    if (!trip) return;

    try {
      // Update local state optimistically
      addItemToDay(dayIndex, newItem);
      console.log('Activity added from recommendation:', newItem, 'to day:', dayIndex);
    } catch (error) {
      console.error('Error updating trip after adding activity:', error);
    }
  };



  const handleSaveDay = async (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => {
    if (!trip) return;

    setIsSavingDay(true);
    try {
      // Call backend API to add day
      const response = await TripPlannerAPI.addDayToTrip(trip.tripId, dayData);
      
      if (response.success) {
        // Update local state optimistically (focusOnDay is called inside addDayToTrip)
        addDayToTrip(response.day);
        setIsAddDayModalOpen(false);
        showToast('Day added');
        console.log('New day added successfully:', response.day);
      } else {
        throw new Error(response.message || 'Failed to add day');
      }
    } catch (error) {
      console.error('Error adding day:', error);
      alert(`Failed to add day: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingDay(false);
    }
  };

  const handleSaveDayAndShowRecommendations = async (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => {
    if (!trip) return;

    setIsSavingDay(true);
    try {
      // Call backend API to add day
      const response = await TripPlannerAPI.addDayToTrip(trip.tripId, dayData);
      
      if (response.success) {
        const newDay = response.day;
        
        // Update local state optimistically using addDayToTrip
        // This will handle sorting and selection
        addDayToTrip(newDay);
        setIsAddDayModalOpen(false);
        showToast('Day added');
        console.log('New day added successfully:', newDay);
        
        // Wait for state update and DOM render, then open recommendations modal
        // Use longer timeout to ensure DateScroller has rendered the new day
        setTimeout(() => {
          // Find the day index in the updated trip state
          // We need to access the latest trip state, so we'll use a refetch or check the current trip
          // Since addDayToTrip already sets the selected day, we just need to open the modal
          setIsRecommendationsModalOpen(true);
        }, 300);
      } else {
        throw new Error(response.message || 'Failed to add day');
      }
    } catch (error) {
      console.error('Error adding day:', error);
      alert(`Failed to add day: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingDay(false);
    }
  };

  const handleAIGenerateItemDetails = async (prompt: string) => {
    if (!trip) return;
    
    setIsLoadingAI(true);
    try {
      // Get the day date for the selected day
      const targetDay = trip.days?.[selectedDayIndexForModal];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      // Call backend API to generate item with AI
      const response = await TripPlannerAPI.aiGenerateItem(trip.tripId, targetDay.dayId, prompt);
      
      if (response.success) {
        // Return the generated item data to fill the form
        return response.item;
      } else {
        throw new Error(response.message || 'Failed to generate item with AI');
      }
    } catch (error) {
      console.error('Error generating item with AI:', error);
      throw error;
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleAIGenerateItemPreview = async (prompt: string): Promise<any> => {
    if (!trip) throw new Error('No trip available');
    
    setIsLoadingAI(true);
    try {
      // Get the day date for the selected day
      const targetDay = trip.days?.[selectedDayIndexForModal];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      // Call backend API to generate item preview with AI (without saving)
      const response = await TripPlannerAPI.aiGenerateItemPreview(trip.tripId, targetDay.dayId, prompt);
      
      if (response.success) {
        // Return the generated item data to fill the form
        return response.item;
      } else {
        throw new Error(response.message || 'Failed to generate item preview with AI');
      }
    } catch (error) {
      console.error('Error generating item preview with AI:', error);
      throw error;
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleAIEditItem = async (editDescription: string) => {
    if (!trip || !editingItem) return;
    
    setIsLoadingAI(true);
    try {
      // Get the day date for the selected day
      const targetDay = trip.days?.[selectedDayIndexForModal];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      // Call backend API to edit item with AI
      const response = await TripPlannerAPI.aiEditItem(
        trip.tripId, 
        targetDay.dayId, 
        editingItem.itemId, 
        editDescription
      );
      
      if (response.success && response.item) {
        console.log('AI Edit API Response:', response);
        // Return the edited item data to fill the form
        return response.item;
      } else {
        throw new Error(response.message || 'Failed to edit item with AI');
      }
    } catch (error) {
      console.error('Error editing item with AI:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(errorMessage);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleAIEditDay = async (editDescription: string) => {
    if (!trip) return;
    
    setIsLoadingAI(true);
    try {
      const targetDay = trip.days?.[selectedDayIndexForModal];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      // Call backend API to edit day with AI
      const response = await TripPlannerAPI.aiEditDay(
        trip.tripId,
        targetDay.dayId,
        editDescription
      );
      
      if (response.success && response.day) {
        console.log('AI Edit Day API Response:', response);
        // Return the edited day data to fill the form
        return response.day;
      } else {
        throw new Error(response.message || 'Failed to edit day with AI');
      }
    } catch (error) {
      console.error('Error editing day with AI:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(errorMessage);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleSaveItineraryItem = async (itemData: {
    name: string;
    placeName: string;
    description: string;
    startTime: string;
    endTime: string;
    location: { lat: number; lon: number };
    itemType: string;
    estimatedCost: number;
  }) => {
    if (!trip) return;

    setIsSavingItem(true);
    try {
      // Get the day date for the selected day
      const targetDay = trip.days?.[selectedDayIndexForModal];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      // Call backend API to add item
      const response = await TripPlannerAPI.addItemToDay(trip.tripId, targetDay.dayId, itemData);
      
      if (response.success) {
        // Update local state optimistically
        addItemToDay(selectedDayIndexForModal, response.item);
        setIsAddItemModalOpen(false);
        showToast('Activity added');
        console.log('New itinerary item added successfully:', response.item);
      } else {
        throw new Error(response.message || 'Failed to add item');
      }
    } catch (error) {
      console.error('Error adding itinerary item:', error);
      alert(`Failed to add itinerary item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingItem(false);
    }
  };




  const handleAIGenerateSummary = async (summary: string): Promise<{ summary: string; lodgingInfo: string }> => {
    if (!trip) throw new Error('No trip available');
    
    setIsGeneratingSummary(true);
    try {
      const response = await TripPlannerAPI.aiGenerateDaySummary(trip.tripId, summary);
      
      if (response.success) {
        return {
          summary: response.summary,
          lodgingInfo: response.lodgingInfo
        };
      } else {
        throw new Error(response.message || 'Failed to generate enhanced summary');
      }
    } catch (error) {
      console.error('Error generating enhanced summary:', error);
      throw error;
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleAIGenerateNewDay = async (prompt: string, suggestedDate?: string): Promise<{ dayDate: string; title: string; summary: string; lodgingInfo: string }> => {
    if (!trip) throw new Error('No trip available');
    
    try {
      const response = await TripPlannerAPI.aiGenerateNewDay(trip.tripId, prompt, suggestedDate);
      
      if (response.success) {
        return {
          dayDate: response.dayDate,
          title: response.title,
          summary: response.summary,
          lodgingInfo: response.lodgingInfo || ''
        };
      } else {
        throw new Error(response.message || 'Failed to generate day with AI');
      }
    } catch (error) {
      console.error('Error generating new day with AI:', error);
      throw error;
    }
  };

  const handleAIGenerateWithActivities = async (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => {
    if (!trip) return;

    setIsLoadingAI(true);
    try {
      const response = await TripPlannerAPI.addDayWithActivities(trip.tripId, dayData);
      
      if (response.success) {
        // Update local state optimistically (focusOnDay is called inside addDayToTrip)
        addDayToTrip(response.day);
        setIsAddDayModalOpen(false);
        showToast('Day added');
        console.log('Day with activities added successfully:', response.day);
      } else {
        throw new Error(response.message || 'Failed to add day with activities');
      }
    } catch (error) {
      console.error('Error adding day with activities:', error);
      alert(`Failed to add day with activities: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleEditDay = (dayIndex: number) => {
    setSelectedDayIndexForModal(dayIndex);
    setIsEditDayModalOpen(true);
  };

  const handleUpdateDay = async (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => {
    if (!trip) return;
    
    setIsUpdatingDay(true);
    try {
      const targetDay = trip.days?.[selectedDayIndexForModal];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      const response = await TripPlannerAPI.updateDay(trip.tripId, targetDay.dayId, dayData);
      if (response.success) {
        // Update local state optimistically
        updateDayInTrip(selectedDayIndexForModal, response.day);
        setIsEditDayModalOpen(false);
        showToast('Day updated');
        console.log('Day updated successfully:', response.day);
      } else {
        throw new Error(response.message || 'Failed to update day');
      }
    } catch (error) {
      console.error('Error updating day:', error);
      alert(`Failed to update day: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUpdatingDay(false);
    }
  };

  const handleUpdateDayAndShowRecommendations = async (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => {
    if (!trip) return;
    
    setIsUpdatingDay(true);
    try {
      const targetDay = trip.days?.[selectedDayIndexForModal];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      const response = await TripPlannerAPI.updateDay(trip.tripId, targetDay.dayId, dayData);
      if (response.success) {
        // Update local state optimistically
        updateDayInTrip(selectedDayIndexForModal, response.day);
        setIsEditDayModalOpen(false);
        
        // Open recommendations modal for the updated day
        setIsRecommendationsModalOpen(true);
        showToast('Day updated');
        console.log('Day updated and recommendations opened:', response.day);
      } else {
        throw new Error(response.message || 'Failed to update day');
      }
    } catch (error) {
      console.error('Error updating day:', error);
      alert(`Failed to update day: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUpdatingDay(false);
    }
  };

  const handleUpdateDayWithActivities = async (dayData: { dayDate: string; title: string; summary: string; lodgingInfo: string }) => {
    if (!trip) return;
    
    setIsLoadingAI(true);
    try {
      const targetDay = trip.days?.[selectedDayIndex];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      // Use the new override endpoint that deletes all existing activities and generates new ones
      const response = await TripPlannerAPI.overrideDayWithActivities(trip.tripId, targetDay.dayId, dayData);
      if (response.success) {
        // Update local state optimistically
        updateDayInTrip(selectedDayIndex, response.day);
        setIsEditDayModalOpen(false);
        showToast('Day updated');
        console.log('Day overridden with new activities:', response.day);
      } else {
        throw new Error(response.message || 'Failed to override day with activities');
      }
    } catch (error) {
      console.error('Error overriding day with activities:', error);
      alert(`Failed to override day: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleDeleteDayDirect = async (dayIndex: number) => {
    if (!trip) return;
    
    setIsDeletingDay(true);
    try {
      const targetDay = trip.days?.[dayIndex];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }

      const deletedDayId = targetDay.dayId;
      const response = await TripPlannerAPI.deleteDay(trip.tripId, deletedDayId);
      if (response.success) {
        // Update local state optimistically
        deleteDayFromTrip(dayIndex);
        
        // If the deleted day was selected, update selectedDayId
        if (selectedDayId === deletedDayId) {
          const remainingDays = trip.days.filter((_, idx) => idx !== dayIndex);
          if (remainingDays.length > 0) {
            // Select the first remaining day, or the day at the same index if available
            const newSelectedIndex = dayIndex < remainingDays.length ? dayIndex : remainingDays.length - 1;
            setSelectedDayId(remainingDays[newSelectedIndex].dayId);
          } else {
            // No days left
            setSelectedDayId(null);
          }
        }
        
        // Refetch trip to ensure UI is updated
        await refetch();
        
        showToast('Day deleted');
        console.log('Day deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete day');
      }
    } catch (error) {
      console.error('Error deleting day:', error);
      alert(`Failed to delete day: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeletingDay(false);
    }
  };

  const handleEditTrip = () => {
    setIsEditTripModalOpen(true);
  };

  const handleUpdateTrip = async (tripData: {
    destination: string;
    startDate: string;
    endDate: string;
    travelers: number;
    budget: number;
    interests: string[];
    coverImage?: string;
  }) => {
    if (!trip) return;
    
    setIsUpdatingTrip(true);
    try {
      const response = await TripPlannerAPI.updateTrip(trip.tripId, tripData);
      if (response.success) {
        // Update local state optimistically
        updateTrip(currentTrip => {
          if (!currentTrip || !response.trip) return currentTrip;
          return {
            ...currentTrip,
            destination: response.trip.destination,
            startDate: response.trip.startDate,
            endDate: response.trip.endDate,
            travelers: response.trip.travelers,
            budget: response.trip.budget,
            interests: response.trip.interests,
            coverImage: response.trip.coverImage
          };
        });
        setIsEditTripModalOpen(false);
        showToast('Trip updated');
        console.log('Trip updated successfully:', response.trip);
      } else {
        throw new Error(response.message || 'Failed to update trip');
      }
    } catch (error) {
      console.error('Error updating trip:', error);
      alert(`Failed to update trip: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUpdatingTrip(false);
    }
  };

  const handleDeleteTrip = async () => {
    if (!trip) return;
    
    setIsDeletingTrip(true);
    try {
      const response = await TripPlannerAPI.deleteTrip(trip.tripId);
      if (response.success) {
        showToast('Trip deleted');
        // Redirect to trips page after successful deletion without full reload to keep auth context
        navigate('/trips', { replace: true });
        console.log('Trip deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete trip');
      }
    } catch (error) {
      console.error('Error deleting trip:', error);
      alert(`Failed to delete trip: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeletingTrip(false);
    }
  };

  const handleEditItem = (item: any, dayIndex: number) => {
    setEditingItem(item);
    setSelectedDayIndexForModal(dayIndex);
    setIsAddItemModalOpen(true);
  };

  const handleUpdateItem = async (itemData: {
    name: string;
    placeName: string;
    description: string;
    startTime: string;
    endTime: string;
    location: { lat: number; lon: number };
    itemType: string;
    estimatedCost: number;
  }) => {
    if (!trip || !editingItem) return;
    
    setIsSavingItem(true);
    try {
      // Preserve isDone status when updating
      const updateData = {
        ...itemData,
        isDone: editingItem.isDone ?? false
      };
      
      // Call API to update item in database
      const response = await TripPlannerAPI.updateItem(
        trip.tripId,
        trip.days[selectedDayIndexForModal].dayId,
        editingItem.itemId,
        updateData
      );
      
      if (response.success) {
        // Update local state optimistically
        updateItemInDay(selectedDayIndexForModal, editingItem.itemId, updateData);
        setIsAddItemModalOpen(false);
        setEditingItem(null);
        showToast('Activity updated');
        console.log('Item updated successfully');
      } else {
        throw new Error(response.message || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert(`Failed to update item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingItem(false);
    }
  };

  const showToast = (message: string) => {
    // Clear any existing timeout
    if (toastTimeout) {
      clearTimeout(toastTimeout);
    }
    
    // Set the message
    setToastMessage(message);
    
    // Auto-dismiss after 1.5 seconds
    const timeout = setTimeout(() => {
      setToastMessage(null);
      setToastTimeout(null);
    }, 1500);
    
    setToastTimeout(timeout);
  };

  const handleDeleteItemDirect = async (item: any, dayIndex: number) => {
    if (!trip) return;
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }
    
    setIsDeletingItem(true);
    try {
      const targetDay = trip.days?.[dayIndex];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }
      const response = await TripPlannerAPI.deleteItem(
        trip.tripId,
        targetDay.dayId,
        item.itemId
      );
      if (response.success) {
        deleteItemFromDay(dayIndex, item.itemId);
        console.log('Item deleted successfully');
        showToast('Activity was deleted');
      } else {
        throw new Error(response.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert(`Failed to delete item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeletingItem(false);
    }
  };

  const handleMarkItemDone = async (item: any, dayIndex: number) => {
    if (!trip) return;
    
    try {
      const targetDay = trip.days?.[dayIndex];
      if (!targetDay) {
        throw new Error('Selected day not found');
      }
      
      // Toggle isDone status (default to false if undefined)
      const newIsDone = !(item.isDone ?? false);
      
      // Prepare update data with all required fields
      const updateData = {
        name: item.name,
        placeName: item.placeName,
        description: item.description,
        startTime: item.startTime,
        endTime: item.endTime,
        location: item.location,
        itemType: item.itemType,
        estimatedCost: item.estimatedCost,
        officialUrl: item.officialUrl,
        isDone: newIsDone
      };
      
      const response = await TripPlannerAPI.updateItem(
        trip.tripId,
        targetDay.dayId,
        item.itemId,
        updateData
      );
      
      if (response.success) {
        // Update local state optimistically
        updateItemInDay(dayIndex, item.itemId, { isDone: newIsDone });
        console.log(`Item marked as ${newIsDone ? 'done' : 'not done'} successfully`);
        showToast(newIsDone ? 'Activity marked as done' : 'Activity marked as undone');
      } else {
        throw new Error(response.message || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error marking item as done:', error);
      alert(`Failed to update item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Authentication check - must be authenticated to view trips
  if (authLoading) {
    return (
      <div className="flex min-h-screen bg-white items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="loading-spinner h-12 w-12 mx-auto mb-6"></div>
          <p className="text-lg text-airbnb-600 font-medium">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen bg-white items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="text-yellow-500 text-6xl mb-6">🔐</div>
          <h1 className="text-3xl font-bold text-airbnb-800 mb-3">Authentication Required</h1>
          <p className="text-airbnb-600 mb-4 text-lg">Please sign in to view this trip.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen bg-white items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="loading-spinner h-12 w-12 mx-auto mb-6"></div>
          <p className="text-lg text-airbnb-600 font-medium">Loading your trip...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-white items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="text-red-500 text-6xl mb-6">⚠️</div>
          <h1 className="text-3xl font-bold text-airbnb-800 mb-3">Error Loading Trip</h1>
          <p className="text-airbnb-600 mb-8 text-lg">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary btn-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  console.log('🔍 TripViewPage render - isMobile:', isMobile, 'mobileView:', mobileView, 'bottomSheetOpen:', bottomSheetOpen, 'selectedMapItem:', selectedMapItem);

  // Get selected day data
  const selectedDay = trip?.days?.find(day => day.dayId === selectedDayId) || null;
  const selectedDayIndex = trip?.days?.findIndex(day => day.dayId === selectedDayId) ?? -1;

  // Format date range
  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = startDate.getDate();
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
    const endDay = endDate.getDate();
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  };

  const handleDateSelect = (dayId: string) => {
    setSelectedDayId(dayId);
    // Expand the selected day
    const dayIdx = trip?.days?.findIndex(day => day.dayId === dayId) ?? -1;
    if (dayIdx !== -1) {
      setExpandedDays(prev => new Set(prev).add(dayIdx));
      
      // Scroll to day description on mobile only when sticky header is visible
      if (isMobile && showStickyHeader) {
        setTimeout(() => {
          const dayDescriptionElement = document.getElementById(`day-description-${dayId}`);
          const stickyHeaderElement = document.querySelector('[data-sticky-header]') as HTMLElement;
          
          if (dayDescriptionElement && stickyHeaderElement) {
            const stickyHeaderHeight = stickyHeaderElement.offsetHeight;
            const elementPosition = dayDescriptionElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - stickyHeaderHeight - 70; // 8px extra spacing
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F2FBF9]" style={{ paddingTop: isMobile ? '81px' : '0' }}>

      {/* Hero Section with Background Image */}
      <div 
        ref={headerRef}
        className="relative w-full overflow-hidden"
        style={{ height: isMobile ? '50vh' : '35vh' }}
      >
        {/* Cover Image */}
        <div className="absolute inset-0">
          <img
            src={trip?.coverImage || `https://source.unsplash.com/1600x900/?${encodeURIComponent(trip?.destination || 'travel')},destination`}
            alt={trip?.destination}
            className="w-full h-full object-cover"
          />
          {/* Gradient Overlay */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 80%)'
            }}
          />
        </div>

        {/* Trip Details Overlay */}
        <div className="absolute inset-0 flex flex-col p-4" style={{ paddingTop: isMobile ? '60px' : '32px' }}>
          {/* Top - Trip Name with Edit Icon */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <h1 
                className="text-white"
                style={{ 
                  fontSize: isMobile ? '36px' : '48px',
                  lineHeight: '1.1',
                  fontWeight: 400,
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.6), 0 4px 16px rgba(0, 0, 0, 0.4), 0 0 24px rgba(0, 0, 0, 0.3)',
                  letterSpacing: '-0.02em'
                }}
              >
                {trip?.destination || 'Trip Details'}
              </h1>
              <button
                onClick={handleEditTrip}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
                aria-label="Edit trip name"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Dates and Travelers - Right below trip name */}
          <div className="space-y-1">
            <div 
              className="text-white"
              style={{ 
                fontSize: '16px', 
                fontWeight: 400,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                letterSpacing: '0.01em'
              }}
            >
              <span>
                {trip?.startDate && trip?.endDate 
                  ? `${formatDateRange(trip.startDate, trip.endDate)} (${trip?.days?.length || 0} days)`
                  : 'No dates set'}
              </span>
            </div>
            <div 
              className="text-white"
              style={{ 
                fontSize: '16px', 
                fontWeight: 400,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                letterSpacing: '0.01em'
              }}
            >
              <span>{trip?.travelers || 1} {trip?.travelers === 1 ? 'Adult' : 'Adults'}</span>
            </div>
          </div>

          {/* Bottom Right - Share and Participants */}
          <div className="absolute right-4 flex items-center gap-2" style={{ bottom: isMobile ? '60px' : '20px' }}>
            <button
              className="rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.04)] flex items-center justify-center"
              style={{ width: '38px', height: '38px' }}
              aria-label="Share"
            >
              <svg className="w-5 h-5 text-[#0B1412]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
            {/* Participant avatars placeholder */}
            <div className="flex -space-x-2.5">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-700 to-primary-500 border-2 border-white flex items-center justify-center text-white text-xs font-semibold">
                {user ? getUserName()?.charAt(0).toUpperCase() : 'U'}
              </div>
              <div className="w-9 h-9 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-gray-600 text-xs font-semibold">
                +1
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Header - Shows when scrolling down on mobile */}
      {isMobile && showStickyHeader && trip && (
        <div data-sticky-header className="fixed left-0 right-0 z-40 bg-[#F2FBF9] border-b border-[#EAEFED] shadow-lg transition-all duration-300" style={{ top: '81px' }}>
          <div className="px-4 pt-3 pb-2">
            <h1 
              className="text-[#0B1412]"
              style={{ 
                fontSize: '28px',
                lineHeight: '1.1',
                fontWeight: 400,
                letterSpacing: '-0.02em'
              }}
            >
              {trip?.destination || 'Trip Details'}
            </h1>
          </div>
          {trip?.days && trip.days.length > 0 && (
            <div className="">
              <DateScroller
                days={trip.days}
                selectedDayId={selectedDayId}
                onDateSelect={handleDateSelect}
                noCurve={true}
                scrollLeft={dateScrollerScrollLeft}
                onScrollChange={setDateScrollerScrollLeft}
              />
            </div>
          )}
        </div>
      )}

      {/* Date Scroller - Mobile First Design (Regular View) */}
      {isMobile && !showStickyHeader && trip?.days && trip.days.length > 0 && (
        <DateScroller
          days={trip.days}
          selectedDayId={selectedDayId}
          onDateSelect={handleDateSelect}
          scrollLeft={dateScrollerScrollLeft}
          onScrollChange={setDateScrollerScrollLeft}
        />
      )}

      {/* Main Content - Mobile First Layout */}
      <div className={`${isMobile ? 'flex flex-col' : 'flex mx-auto px-8 mt-8 gap-8'}`} style={{ maxWidth: '99rem' }}>
        {/* Itinerary Content */}
        <div className={`${isMobile ? 'w-full' : 'flex-1'} ${isMobile && mobileView !== 'itinerary' ? 'hidden' : ''} relative`}>
          {/* Desktop: Date Scroller at top of flex-1 div */}
          {!isMobile && trip?.days && trip.days.length > 0 && (
            <div className="absolute top-0 left-0 right-0 z-10" style={{ height: 'auto' }}>
              <DateScroller
                days={trip.days}
                selectedDayId={selectedDayId}
                onDateSelect={handleDateSelect}
                noCurve={true}
                scrollLeft={dateScrollerScrollLeft}
                onScrollChange={setDateScrollerScrollLeft}
              />
            </div>
          )}
          
          {/* Desktop: Fixed Add Button with Menu (Same as Mobile) */}
          {!isMobile && (
            <div className="fixed z-50" style={{
              bottom: '24px',
              left: '1%',
            }}>
              <div className="relative">
                {/* Add Menu Popup */}
                {showAddMenu && (
                  <div 
                    data-add-menu
                    className="absolute bottom-full left-0 mb-3 bg-white border border-[#E3E8E5] rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] overflow-hidden"
                    style={{ 
                      animation: 'fadeInUp 0.2s ease-out',
                      minWidth: '160px',
                      width: 'max-content'
                    }}
                  >
                    {/* New Activity Option */}
                    <button
                      onClick={() => {
                        setShowAddMenu(false);
                        if (selectedDay) {
                          setSelectedDayIndexForModal(selectedDayIndex);
                          setEditingItem(null);
                          setIsAddItemModalOpen(true);
                        } else if (trip?.days && trip.days.length > 0) {
                          // If no selected day, use first day
                          const firstDayIndex = 0;
                          setSelectedDayIndexForModal(firstDayIndex);
                          setEditingItem(null);
                          setIsAddItemModalOpen(true);
                        }
                      }}
                      className={`w-full px-5 py-4 text-left flex items-center gap-3 transition-colors duration-200 border-b border-[#E3E8E5] whitespace-nowrap ${
                        !trip || !trip.days || trip.days.length === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-[#F2FBF9]'
                      }`}
                      disabled={!trip || !trip.days || trip.days.length === 0}
                    >
                      <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-lg">📍</span>
                      </div>
                      <div className="text-[#0B1412] font-semibold text-base">New Activity</div>
                    </button>
                    
                    {/* New Day Option */}
                    <button
                      onClick={() => {
                        setShowAddMenu(false);
                        setIsAddDayModalOpen(true);
                      }}
                      className="w-full px-5 py-4 text-left hover:bg-[#F2FBF9] flex items-center gap-3 transition-colors duration-200 whitespace-nowrap"
                    >
                      <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-lg">📅</span>
                      </div>
                      <div className="text-[#0B1412] font-semibold text-base">New Day</div>
                    </button>
                  </div>
                )}

                {/* Add New Button */}
                <button
                  data-add-button
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  className={`rounded-full bg-white border border-[#E3E8E5] shadow-[0_6px_12px_rgba(0,0,0,0.06)] flex items-center justify-center transition-all hover:shadow-[0_10px_18px_rgba(0,0,0,0.08)] hover:bg-[#F2FBF9] hover:border-primary-300 hover:scale-110 ${
                    showAddMenu ? 'bg-primary-50 border-primary-300' : ''
                  }`}
                  style={{ width: '48px', height: '48px' }}
                  aria-label="Add new"
                  title="Add new"
                >
                  <svg 
                    className={`text-[#0B1412] transition-transform duration-200 ${showAddMenu ? 'rotate-45' : ''}`} 
                    style={{ width: '20px', height: '20px' }} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {trip ? (
            <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={onDragStart} onDragOver={onDragOver} onDragMove={onDragMove} onDragEnd={onDragEnd}>
              {isMobile ? (
                // Mobile: Show only selected day
                selectedDay ? (
                  <div className="px-2 pb-24">
                    {/* City Section */}
                    <div 
                      id={`day-description-${selectedDay.dayId}`}
                      className="bg-[#F2FBF9] px-4 py-5"
                      style={{ borderRadius: '24px 24px 0 0' }}
                    >
                      <div className="mb-3">
                        {/* Day Title */}
                        <h2 
                          className="text-[#0B1412] font-bold mb-2"
                          style={{ fontSize: '28px', fontWeight: 700 }}
                        >
                          {selectedDay.title || 'Day'}
                        </h2>
                        {/* Weather, Edit, Delete Row */}
                        <div className="flex items-center justify-between">
                          {/* Weather placeholder */}
                          <div className="flex items-center gap-1.5 text-[#0B1412]" style={{ fontSize: '15px' }}>
                            <span>☀️</span>
                            <span>25°C</span>
                          </div>
                          {/* Edit and Delete buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditDay(selectedDayIndex)}
                              className="w-5 h-5 flex items-center justify-center"
                              aria-label="Edit day"
                            >
                              <svg className="w-4 h-4 text-[#6F7974]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this day? This action cannot be undone.')) {
                                  handleDeleteDayDirect(selectedDayIndex);
                                }
                              }}
                              className="w-5 h-5 flex items-center justify-center text-[#6F7974] hover:text-[#0B1412]"
                              aria-label="Delete day"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Description */}
                      {selectedDay.summary && (
                        <p 
                          className="text-[#6F7974] mb-3"
                          style={{ fontSize: '15px', lineHeight: '1.5' }}
                        >
                          {selectedDay.summary}
                        </p>
                      )}
                      
                      {/* Lodging Info */}
                      {selectedDay.lodgingInfo && (
                        <div 
                          className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-[#E3E8E5]"
                          style={{ fontSize: '14px' }}
                        >
                          <span className="text-lg">🏨</span>
                          <span className="text-[#0B1412] font-medium">{selectedDay.lodgingInfo}</span>
                        </div>
                      )}
                    </div>

                    {/* Activity Cards */}
                    <div className="px-4 space-y-4 pt-4">
                      {selectedDay.itinerary && selectedDay.itinerary.length > 0 ? (
                        <SortableContext
                          items={getOrderForDay(selectedDayIndex).map(id => id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {getOrderForDay(selectedDayIndex)
                            .map((itemId, itemIndex) => {
                              const item = selectedDay.itinerary.find((it) => it.itemId === itemId);
                              if (!item) return null;
                              const placeholderIndex = dragPlaceholderByDay[selectedDayIndex] ?? null;
                              return (
                                <React.Fragment key={item.itemId}>
                                  {placeholderIndex !== null && itemIndex === placeholderIndex && (
                                    <div className="rounded-xl border-2 border-dashed border-primary-700 bg-primary-50/60 h-20 transition-all" />
                                  )}
                                  <SortableItineraryItem
                                    item={item}
                                    isSelected={selectedItemId === item.itemId}
                                    onClick={() => handleItemSelect(item.itemId)}
                                    onEdit={(it) => handleEditItem(it, selectedDayIndex)}
                                    onDelete={(it) => handleDeleteItemDirect(it, selectedDayIndex)}
                                    onMarkDone={(it) => handleMarkItemDone(it, selectedDayIndex)}
                                  />
                                </React.Fragment>
                              );
                            })
                            .filter(Boolean)}
                          {(() => {
                            const placeholderIndex = dragPlaceholderByDay[selectedDayIndex] ?? null;
                            const orderLength = getOrderForDay(selectedDayIndex).length;
                            if (placeholderIndex !== null && placeholderIndex >= orderLength) {
                              return (
                                <div key="placeholder-end" className="rounded-xl border-2 border-dashed border-primary-700 bg-primary-50/60 h-20 transition-all" />
                              );
                            }
                            return null;
                          })()}
                        </SortableContext>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-[#6F7974] mb-4">No activities for this day yet.</p>
                          <button
                            onClick={() => handleAddItineraryItem(selectedDayIndex)}
                            className="btn-primary"
                          >
                            + Add Activity
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16 px-4">
                    <div className="w-16 h-16 bg-[#E3E8E5] rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📅</span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#0B1412] mb-2">No days planned yet</h3>
                    <p className="text-[#6F7974] mb-6">Start building your itinerary by adding your first day.</p>
                    <button
                      onClick={handleAddDay}
                      className="btn-primary"
                    >
                      + Add Your First Day
                    </button>
                  </div>
                )
              ) : (
                // Desktop: Show mobile day design for selected day
                selectedDay ? (
                  <div className="px-8 py-4" style={{ paddingTop: trip?.days && trip.days.length > 0 ? '100px' : '16px' }}>
                    {/* City Section */}
                    <div 
                      id={`day-description-${selectedDay.dayId}`}
                      className="bg-[#F2FBF9] px-4 py-5 rounded-2xl mb-4"
                    >
                      <div className="mb-3">
                        {/* Day Title */}
                        <h2 
                          className="text-[#0B1412] font-bold mb-2"
                          style={{ fontSize: '28px', fontWeight: 700 }}
                        >
                          {selectedDay.title || 'Day'}
                        </h2>
                        {/* Weather, Edit, Delete Row */}
                        <div className="flex items-center justify-between">
                          {/* Weather placeholder */}
                          <div className="flex items-center gap-1.5 text-[#0B1412]" style={{ fontSize: '15px' }}>
                            <span>☀️</span>
                            <span>25°C</span>
                          </div>
                          {/* Edit and Delete buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditDay(selectedDayIndex)}
                              className="w-5 h-5 flex items-center justify-center"
                              aria-label="Edit day"
                            >
                              <svg className="w-4 h-4 text-[#6F7974]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this day? This action cannot be undone.')) {
                                  handleDeleteDayDirect(selectedDayIndex);
                                }
                              }}
                              className="w-5 h-5 flex items-center justify-center text-[#6F7974] hover:text-[#0B1412]"
                              aria-label="Delete day"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Description */}
                      {selectedDay.summary && (
                        <p 
                          className="text-[#6F7974] mb-3"
                          style={{ fontSize: '15px', lineHeight: '1.5' }}
                        >
                          {selectedDay.summary}
                        </p>
                      )}
                      
                      {/* Lodging Info */}
                      {selectedDay.lodgingInfo && (
                        <div 
                          className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-[#E3E8E5]"
                          style={{ fontSize: '14px' }}
                        >
                          <span className="text-lg">🏨</span>
                          <span className="text-[#0B1412] font-medium">{selectedDay.lodgingInfo}</span>
                        </div>
                      )}
                    </div>

                    {/* Activity Cards */}
                    <div className="px-4 space-y-4">
                      {selectedDay.itinerary && selectedDay.itinerary.length > 0 ? (
                        <SortableContext
                          items={getOrderForDay(selectedDayIndex).map(id => id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {getOrderForDay(selectedDayIndex)
                            .map((itemId, itemIndex) => {
                              const item = selectedDay.itinerary.find((it) => it.itemId === itemId);
                              if (!item) return null;
                              const placeholderIndex = dragPlaceholderByDay[selectedDayIndex] ?? null;
                              return (
                                <React.Fragment key={item.itemId}>
                                  {placeholderIndex !== null && itemIndex === placeholderIndex && (
                                    <div className="rounded-xl border-2 border-dashed border-primary-700 bg-primary-50/60 h-20 transition-all" />
                                  )}
                                  <SortableItineraryItem
                                    item={item}
                                    isSelected={selectedItemId === item.itemId}
                                    onClick={() => handleItemSelect(item.itemId)}
                                    onEdit={(it) => handleEditItem(it, selectedDayIndex)}
                                    onDelete={(it) => handleDeleteItemDirect(it, selectedDayIndex)}
                                    onMarkDone={(it) => handleMarkItemDone(it, selectedDayIndex)}
                                  />
                                </React.Fragment>
                              );
                            })
                            .filter(Boolean)}
                          {(() => {
                            const placeholderIndex = dragPlaceholderByDay[selectedDayIndex] ?? null;
                            const orderLength = getOrderForDay(selectedDayIndex).length;
                            if (placeholderIndex !== null && placeholderIndex >= orderLength) {
                              return (
                                <div key="placeholder-end" className="rounded-xl border-2 border-dashed border-primary-700 bg-primary-50/60 h-20 transition-all" />
                              );
                            }
                            return null;
                          })()}
                        </SortableContext>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-[#6F7974] mb-4">No activities for this day yet.</p>
                          <button
                            onClick={() => handleAddItineraryItem(selectedDayIndex)}
                            className="btn-primary"
                          >
                            + Add Activity
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 bg-[#E3E8E5] rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📅</span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#0B1412] mb-2">No days planned yet</h3>
                    <p className="text-[#6F7974] mb-6">Start building your itinerary by adding your first day.</p>
                    <button
                      onClick={handleAddDay}
                      className="btn-primary"
                    >
                      + Add Your First Day
                    </button>
                  </div>
                )
              )}
              {createPortal(
                <DragOverlay
                  dropAnimation={{
                    duration: 350,
                    easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  {activeDragItem ? (
                    <div className="opacity-95">
                      <ItineraryItemCard item={activeDragItem} isSelected dragHandleProps={undefined} isDragging />
                    </div>
                  ) : null}
                </DragOverlay>,
                document.body
              )}
            </DndContext>
          ) : (
            <div className="text-center py-16 px-4">
              <div className="w-16 h-16 bg-[#E3E8E5] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="text-lg font-semibold text-[#0B1412] mb-2">No trip data available</h3>
              <p className="text-[#6F7974]">There was an issue loading this trip.</p>
            </div>
          )}
        </div>

        {/* Right Column - Map */}
        <div 
          className={`${isMobile ? 'fixed inset-0 z-50 w-full h-screen' : 'w-[600px] transition-all duration-300 self-start'} ${
            isMobile 
              ? (mobileView !== 'map' ? 'hidden' : '') 
              : (scrolled ? 'sticky top-20 h-[calc(100vh-6rem)] mt-0' : '-mt-[15vh] h-[68vh]')
          }`}
        >
          {/* Mobile Map Exit Button */}
          {isMobile && mobileView === 'map' && (
            <button
              onClick={() => setMobileView('itinerary')}
              className="absolute top-6 right-6 z-[60] bg-white text-gray-700 px-4 py-3 rounded-full shadow-2xl hover:bg-gray-50 transition-all duration-200 flex items-center gap-2 backdrop-blur-sm bg-opacity-95"
              aria-label="Close Map"
            >
              <span className="text-sm font-semibold">Exit Map</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          <div className={`${isMobile ? 'h-full' : 'bg-white rounded-2xl shadow-lg overflow-hidden h-full'}`}>
            <div className="h-full">
              {trip && trip.days && trip.days.length > 0 ? (
                <MapView 
                  items={trip.days.flatMap(day => day.itinerary || [])} 
                  selectedItemId={selectedItemId || undefined}
                  onItemSelect={isMobile ? handleMapItemClick : handleItemSelect}
                  centerOnItemId={selectedItemId || undefined}
                  days={trip.days}
                  selectedDayId={selectedDayId}
                  onDateSelect={setSelectedDayId}
                  isMobile={isMobile}
                />
              ) : (
                <div className="h-full bg-gradient-to-br from-airbnb-100 to-airbnb-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-airbnb">
                      <span className="text-xl">🗺️</span>
                    </div>
                    <p className="text-airbnb-600 text-sm">Add activities to see them on the map</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Buttons (FABs) - Mobile Only */}
      {isMobile && mobileView === 'itinerary' && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 flex items-end gap-4">
          {/* Add New FAB with Menu */}
          <div className="relative">
            {/* Add Menu Popup */}
            {showAddMenu && (
              <div 
                data-add-menu
                className="absolute bottom-full right-0 mb-3 bg-white border border-[#E3E8E5] rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] overflow-hidden"
                style={{ 
                  animation: 'fadeInUp 0.2s ease-out',
                  minWidth: '160px',
                  width: 'max-content'
                }}
              >
                {/* New Activity Option */}
                <button
                  onClick={() => {
                    setShowAddMenu(false);
                    if (selectedDay) {
                      setSelectedDayIndexForModal(selectedDayIndex);
                      setEditingItem(null);
                      setIsAddItemModalOpen(true);
                    } else if (trip?.days && trip.days.length > 0) {
                      // If no selected day, use first day
                      const firstDayIndex = 0;
                      setSelectedDayIndexForModal(firstDayIndex);
                      setEditingItem(null);
                      setIsAddItemModalOpen(true);
                    }
                  }}
                  className={`w-full px-5 py-4 text-left flex items-center gap-3 transition-colors duration-200 border-b border-[#E3E8E5] whitespace-nowrap ${
                    !trip || !trip.days || trip.days.length === 0
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-[#F2FBF9]'
                  }`}
                  disabled={!trip || !trip.days || trip.days.length === 0}
                >
                  <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">📍</span>
                  </div>
                  <div className="text-[#0B1412] font-semibold text-base">New Activity</div>
                </button>
                
                {/* New Day Option */}
                <button
                  onClick={() => {
                    setShowAddMenu(false);
                    setIsAddDayModalOpen(true);
                  }}
                  className="w-full px-5 py-4 text-left hover:bg-[#F2FBF9] flex items-center gap-3 transition-colors duration-200 whitespace-nowrap"
                >
                  <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">📅</span>
                  </div>
                  <div className="text-[#0B1412] font-semibold text-base">New Day</div>
                </button>
              </div>
            )}

            {/* Add New FAB Button */}
            <button
              data-add-button
              onClick={() => setShowAddMenu(!showAddMenu)}
              className={`rounded-full bg-white border border-[#E3E8E5] shadow-[0_6px_12px_rgba(0,0,0,0.06)] flex items-center justify-center transition-all hover:shadow-[0_10px_18px_rgba(0,0,0,0.08)] ${
                showAddMenu ? 'bg-primary-50 border-primary-300' : ''
              }`}
              style={{ width: '54px', height: '54px' }}
              aria-label="Add new"
            >
              <svg 
                className={`text-[#0B1412] transition-transform duration-200 ${showAddMenu ? 'rotate-45' : ''}`} 
                style={{ width: '22px', height: '22px' }} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          
          {/* Map/Location FAB */}
          <button
            onClick={() => {
              setShowAddMenu(false);
              setMobileView('map');
            }}
            className="rounded-full bg-white border border-[#E3E8E5] shadow-[0_6px_12px_rgba(0,0,0,0.06)] flex items-center justify-center transition-all hover:shadow-[0_10px_18px_rgba(0,0,0,0.08)]"
            style={{ width: '54px', height: '54px' }}
            aria-label="Open map"
          >
            <svg className="text-[#0B1412]" style={{ width: '22px', height: '22px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      )}

      {/* Mobile Bottom Sheet - Show only selected marker info */}
      {isMobile && bottomSheetOpen && selectedMapItem && (() => {
        const item = selectedMapItem;
        const { day, dayIndex } = getDayForItem(item);
        
        if (!item) return null;

        // Get item type emoji
        const getItemEmoji = (type: string) => {
          const emojiMap: Record<string, string> = {
            'attraction': '🏛️',
            'restaurant': '🍽️',
            'transportation': '🚗',
            'lodging': '🏨',
            'shopping': '🛍️',
            'entertainment': '🎭',
            'museum': '🖼️',
          };
          return emojiMap[type] || '📍';
        };

        return (
          <div className="fixed inset-0 z-[70] pointer-events-none">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black bg-opacity-30 pointer-events-none"
            />
            
            {/* Bottom Sheet */}
            <div 
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl transform transition-transform duration-300 ease-out max-h-[30vh] pointer-events-auto"
              onTouchStart={handleBottomSheetTouchStart}
            >
              {/* Handle */}
              <div className="flex justify-center pt-2 pb-1">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              
              {/* Content */}
              <div className="px-6 pb-5 overflow-y-auto max-h-[calc(30vh-1.5rem)]">
                {/* Header with Icon and Name */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center text-2xl shadow-lg">
                    {getItemEmoji(item.itemType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1 leading-tight line-clamp-1">
                      {item.name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="capitalize">{item.itemType}</span>
                      {day && (
                        <>
                          <span>•</span>
                          <span>Day {dayIndex + 1}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleCloseBottomSheet}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Place Name */}
                {item.placeName && (
                  <div className="mb-3 flex items-start gap-2">
                    <svg className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-gray-700 font-medium line-clamp-1">{item.placeName}</p>
                  </div>
                )}

                {/* Description */}
                {item.description && (
                  <div className="mb-3">
                    <p className="text-gray-600 leading-relaxed line-clamp-2">{item.description}</p>
                  </div>
                )}

                {/* Chips row: match ItineraryItemCard styling */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="badge badge-primary text-xs">
                    {item.startTime} - {item.endTime}
                  </span>
                  <span className="badge badge-secondary text-xs">
                    ${ (item.estimatedCost / 100).toFixed(2) }
                  </span>
                  <span className="badge text-xs bg-airbnb-100 text-airbnb-700">
                    {item.itemType}
                  </span>
                </div>

                {/* Actions: Directions and View Site */}
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(item.location?.lat)},${encodeURIComponent(item.location?.lon)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-3 rounded-lg text-xs font-semibold transition-colors shadow-md hover:shadow-lg"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <span>Directions</span>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </a>
                  {item.officialUrl && (
                    <a
                      href={item.officialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-primary-500 hover:bg-primary-600 text-white text-center py-2 px-3 rounded-lg text-xs font-semibold transition-colors shadow-md hover:shadow-lg"
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <span>View Site</span>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modals */}
      <AddDayModal
        isOpen={isAddDayModalOpen}
        onClose={() => setIsAddDayModalOpen(false)}
        onSave={handleSaveDay}
        onSaveAndShowRecommendations={handleSaveDayAndShowRecommendations}
        onAIGenerateWithActivities={handleAIGenerateWithActivities}
        onAIGenerateSummary={handleAIGenerateSummary}
        onAIGenerateNewDay={handleAIGenerateNewDay}
        trip={trip}
        isLoading={isLoadingAI}
        isSaving={isSavingDay}
        isGeneratingSummary={isGeneratingSummary}
      />

      {trip && trip.days && trip.days[selectedDayIndexForModal] && (
        <AddItineraryItemModal
          isOpen={isAddItemModalOpen}
          onClose={() => {
            setIsAddItemModalOpen(false);
            setEditingItem(null);
            // Form data reset is handled by the useEffect above when isAddItemModalOpen becomes false
          }}
          onSave={editingItem ? handleUpdateItem : handleSaveItineraryItem}
          onAIGenerate={handleAIGenerateItemDetails}
          onAIGeneratePreview={handleAIGenerateItemPreview}
          onAIEdit={editingItem ? handleAIEditItem : undefined}
          dayIndex={selectedDayIndexForModal}
          day={trip.days[selectedDayIndexForModal]}
          trip={trip}
          editingItem={editingItem}
          isSaving={isSavingItem}
          isDeleting={isDeletingItem}
          onAddActivity={handleAddActivity}
          formData={addItemFormData}
          setFormData={setAddItemFormData}
        />
      )}

      {trip && trip.days && trip.days[selectedDayIndexForModal] ? (
        <RecommendationsModal
          key={`recommendations-${trip.tripId}-${trip.days[selectedDayIndexForModal].dayId}`}
          isOpen={isRecommendationsModalOpen}
          onClose={() => setIsRecommendationsModalOpen(false)}
          day={trip.days[selectedDayIndexForModal]}
          dayIndex={selectedDayIndexForModal}
          trip={trip}
          onAddActivity={handleAddActivity}
          onOpenManualAdd={() => {
            setIsAddItemModalOpen(true);
          }}
          isLoading={isLoadingAI}
        />
      ) : null}

      {trip && trip.days && trip.days[selectedDayIndexForModal] && (
        <EditDayModal
          isOpen={isEditDayModalOpen}
          onClose={() => setIsEditDayModalOpen(false)}
          day={trip.days[selectedDayIndexForModal]}
          onSave={handleUpdateDay}
          onSaveAndShowRecommendations={handleUpdateDayAndShowRecommendations}
          onAIGenerateWithActivities={handleUpdateDayWithActivities}
          onAIEdit={handleAIEditDay}
          isLoading={isLoadingAI}
          isSaving={isUpdatingDay}
          isDeleting={isDeletingDay}
          isGeneratingAI={isLoadingAI}
        />
      )}

      {trip && (
        <EditTripModal
          isOpen={isEditTripModalOpen}
          onClose={() => setIsEditTripModalOpen(false)}
          trip={trip}
          onSave={handleUpdateTrip}
          onDelete={handleDeleteTrip}
          isSaving={isUpdatingTrip}
          isDeleting={isDeletingTrip}
        />
      )}

      {/* Toast Notification */}
      {toastMessage && createPortal(
        <div className="fixed bottom-6 left-0 right-0 flex justify-center z-50 pointer-events-none">
          <div className="bg-black/70 backdrop-blur-sm text-white px-6 py-3 rounded-full shadow-lg animate-fade-in-out whitespace-nowrap">
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TripViewPage;
